import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useQuery } from "@apollo/client";
import {
  midgardClients,
  WORK_SCHEDULE_DAY_LIST_QUERY,
} from "providers/MidgardProvider";
import {
  convertToCSV,
  downloadCSV,
  getDayOfWeek,
} from "./configurationPageUtils";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ShiftSchedulesHighlightedText } from "./ShiftSchedulesHighlightedText";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";

/**
 * Shift Schedules section for displaying shift schedules data
 */
export const ShiftSchedulesSection: React.FC = () => {
  const { translate } = useFlightTranslation();
  const cKey = useCustomerKey();
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "configName", desc: false },
  ]);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  // Query to get all work schedule days
  const {
    loading: queryLoading,
    error: queryError,
    data,
    refetch,
  } = useQuery(WORK_SCHEDULE_DAY_LIST_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "no-cache", // Use no-cache to ensure fresh data
    onError: err => {
      console.error("Error fetching work schedule days:", err);
      setError("Failed to load work schedule days. Please try again later.");
      setLoading(false);
    },
    onCompleted: () => {
      setLoading(false);
    },
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Effect to trigger immediate fetch when component mounts
  useEffect(() => {
    // Log the entire query result to understand its structure
    console.log("Apollo Query Result:", data);

    if (!data && !queryLoading) {
      refetch().catch(err => {
        console.error("Error refetching data:", err);
      });
    }
  }, [data, queryLoading, refetch]);

  // Debug the sort state whenever it changes
  useEffect(() => {
    if (sorting.length > 0) {
      console.log(
        `Current sort: ${sorting[0].id} ${sorting[0].desc ? "descending" : "ascending"}`
      );
    } else {
      console.log("No sorting applied");
    }
  }, [sorting]);

  // Mock data for testing when real data is unavailable
  const mockWorkSchedules = useMemo(
    () => [
      {
        configName: "4006 Meritrans Organizer",
        days: Array(7)
          .fill(null)
          .map((_, i) => ({
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ][i],
            shiftsCount: i % 2 === 0 ? 3 : 4,
            name: `Day ${i + 1} of 4006 Meritrans Organizer`,
            ordinal: i + 1,
            shifts: Array(i % 2 === 0 ? 3 : 4)
              .fill(null)
              .map((_, j) => ({
                name: `Shift ${j + 1}`,
                ordinal: j + 1,
                startTime: "09:00",
                endTime: "17:00",
              })),
            plays: [],
          })),
      },
      {
        configName: "4008 Syringes",
        days: Array(7)
          .fill(null)
          .map((_, i) => ({
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ][i],
            shiftsCount: i % 2 === 0 ? 3 : 4,
            name: `Day ${i + 1} of 4008 Syringes`,
            ordinal: i + 1,
            shifts: Array(i % 2 === 0 ? 3 : 4)
              .fill(null)
              .map((_, j) => ({
                name: `Shift ${j + 1}`,
                ordinal: j + 1,
                startTime: "08:00",
                endTime: "16:00",
              })),
            plays: [],
          })),
      },
    ],
    []
  );

  // Process the data to group by work schedule config - memoize this expensive operation
  const workSchedules = useMemo(() => {
    // Make sure we have data before trying to process it
    if (!data?.workScheduleDayList || data.workScheduleDayList.length === 0) {
      console.log(
        "No workScheduleDayList data available, using mock data for development"
      );

      // Use mock data in development when real data is unavailable
      if (process.env.NODE_ENV === "development") {
        return mockWorkSchedules;
      } else {
        return [];
      }
    }

    console.log(
      "Processing workScheduleDayList:",
      data.workScheduleDayList.length,
      "items"
    );

    const daysByOrdinal = {};
    data.workScheduleDayList.forEach(day => {
      // Extract the work schedule config name from the day name
      // Format is typically "Day X of Config Name"
      const nameParts = day.name.split(" of ");
      if (nameParts.length > 1) {
        const configName = nameParts[1];

        // Initialize the config entry if it doesn't exist
        if (!daysByOrdinal[configName]) {
          daysByOrdinal[configName] = Array(7).fill(null); // 7 days in a week
        }

        // Ordinals are 1-based, array indices are 0-based
        const dayIndex = day.ordinal - 1;
        if (dayIndex >= 0 && dayIndex < 7) {
          daysByOrdinal[configName][dayIndex] = day;
        }
      }
    });

    // Log the processed data
    const schedules = Object.keys(daysByOrdinal).map(configName => ({
      configName,
      days: daysByOrdinal[configName],
    }));

    console.log("Processed workSchedules:", schedules.length, "schedules");
    return schedules;
  }, [data?.workScheduleDayList, mockWorkSchedules]);

  // Function to toggle row expansion - memoize to prevent recreation on each render
  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows(prevExpandedRows => {
      if (prevExpandedRows.includes(index)) {
        return prevExpandedRows.filter(rowIndex => rowIndex !== index);
      } else {
        return [...prevExpandedRows, index];
      }
    });
  }, []);

  // Handle search input change - memoize callback
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  // Clear search input - memoize callback
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Column definitions for TanStack Table
  const columnHelper = useMemo(
    () => createColumnHelper<(typeof workSchedules)[0]>(),
    []
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor("configName", {
        header: () => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>{translate("Work Schedule")}</span>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {sorting.length > 0 && sorting[0].id === "configName" && (
                <Box component="span" sx={{ display: "flex" }}>
                  {sorting[0].desc ? (
                    <ArrowDownwardIcon
                      fontSize="small"
                      sx={{ color: "primary.main" }}
                    />
                  ) : (
                    <ArrowUpwardIcon
                      fontSize="small"
                      sx={{ color: "primary.main" }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ),
        cell: info => (
          <ShiftSchedulesHighlightedText
            text={info.getValue()}
            highlight={searchTerm}
          />
        ),
        size: 200,
        sortingFn: "text", // Use built-in text sorting
      }),
    ],
    [translate, sorting, columnHelper, searchTerm]
  );

  // Memoize the table for better performance
  const table = useReactTable({
    data: workSchedules,
    columns,
    state: {
      sorting,
      globalFilter: searchTerm,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId);

      // For text values, do a case-insensitive substring match
      if (typeof value === "string") {
        return value.toLowerCase().includes(filterValue.toLowerCase());
      }

      // For configName specifically (special case)
      if (columnId === "configName") {
        const configName = row.original.configName;
        return configName.toLowerCase().includes(filterValue.toLowerCase());
      }

      // For other types, use basic string comparison
      return String(value).toLowerCase().includes(filterValue.toLowerCase());
    },
  });

  // Get the sorted and filtered data - memoize to prevent unnecessary recalculations
  const sortedAndFilteredSchedules = useMemo(() => {
    if (!workSchedules || workSchedules.length === 0) {
      console.log("No workSchedules available for filtering/sorting");
      return [];
    }

    // Clone the array to avoid mutating the original
    let result = [...workSchedules];

    // Filter by search term if one exists
    if (searchTerm) {
      result = result.filter(schedule => {
        return schedule.configName
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      });
      console.log(
        `Filtered to ${result.length} schedules matching "${searchTerm}"`
      );
    }

    // Apply sorting based on current sort state
    if (sorting.length > 0) {
      const sortField = sorting[0].id;
      const isDescending = sorting[0].desc;

      console.log(
        `Sorting by ${sortField} in ${isDescending ? "descending" : "ascending"} order`
      );

      // Sort the results
      result.sort((a, b) => {
        if (sortField === "configName") {
          // For text values, use localeCompare
          const compareResult = a.configName.localeCompare(b.configName);
          return isDescending ? -compareResult : compareResult;
        }
        return 0;
      });
    }

    console.log(`Returning ${result.length} schedules after filtering/sorting`);
    return result;
  }, [workSchedules, searchTerm, sorting]);

  // Toggle sort function
  const toggleSort = useCallback(() => {
    setSorting(prevSorting => {
      // Always sort by configName
      if (prevSorting.length === 0) {
        // No sorting, set to ascending
        return [{ id: "configName", desc: false }];
      } else {
        // Toggle between ascending/descending
        return [{ id: "configName", desc: !prevSorting[0].desc }];
      }
    });
  }, []);

  // Loading state
  if (loading || queryLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <div>
            <Typography variant="h5">{translate("Shift Schedules")}</Typography>
          </div>
        </Box>

        {/* Search Field - styled like SKU table */}
        <Box
          sx={{
            p: 2,
            bgcolor: "#f9f9f9",
            borderBottom: "1px solid #e0e0e0",
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            placeholder={translate("Search work schedules...")}
            value={searchTerm}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {/* Always show table header even when loading */}
        <Box sx={{ mt: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#fafafa" }}>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "1px solid #ddd",
                    cursor: "pointer", // Add cursor pointer to indicate clickable
                    verticalAlign: "middle",
                  }}
                  onClick={toggleSort}
                  onMouseOver={e => {
                    e.currentTarget.style.backgroundColor = "#ebebeb";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.backgroundColor = "#fafafa";
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    {flexRender(
                      columns[0].header,
                      table.getHeaderGroups()[0].headers[0].getContext()
                    )}
                  </Box>
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Monday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Tuesday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Wednesday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Thursday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Friday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Saturday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Sunday")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>
                    {translate("Loading shift schedules...")}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error || queryError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || translate("Failed to load shift schedules.")}
        </Alert>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {translate("Retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <div>
          <Typography variant="h5">{translate("Shift Schedules")}</Typography>
        </div>
        <Tooltip title={translate("Download as CSV")}>
          <IconButton
            onClick={() => {
              // Prepare data for CSV export
              const csvData: Array<{
                workSchedule: string;
                day: string;
                dayOfWeek: string;
                shifts: number;
                shiftDetails: string;
              }> = [];

              // Process each work schedule
              workSchedules.forEach(schedule => {
                // Process each day in the schedule
                schedule.days.forEach((day, index) => {
                  if (day) {
                    // Format shift details
                    let shiftDetails = "";
                    if (day.shifts && day.shifts.length > 0) {
                      shiftDetails = day.shifts
                        .map(
                          shift =>
                            `${shift.name || `Shift ${shift.ordinal}`} (${shift.startTime || "?"}-${shift.endTime || "?"})`
                        )
                        .join(", ");
                    }

                    // Add data for this day
                    csvData.push({
                      workSchedule: schedule.configName,
                      day: day.name || `Day ${index + 1}`,
                      dayOfWeek: day.dayOfWeek || getDayOfWeek(index),
                      shifts: day.shiftsCount || 0,
                      shiftDetails: shiftDetails,
                    });
                  }
                });
              });

              // Generate CSV and trigger download
              if (csvData.length > 0) {
                const csvContent = convertToCSV(csvData);
                downloadCSV(
                  csvContent,
                  `shift_schedules_${new Date().toISOString().split("T")[0]}.csv`
                );
              }
            }}
            color="primary"
            size="small"
            disabled={workSchedules.length === 0}
          >
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search Field - styled like SKU table */}
      <Box sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}>
        <TextField
          fullWidth
          placeholder={translate("Search work schedules...")}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {sortedAndFilteredSchedules.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {searchTerm
            ? translate("No work schedules found matching your search.")
            : translate("No work schedules found.")}
        </Alert>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#ebebeb" }}>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "1px solid #ddd",
                    cursor: "pointer", // Add cursor to indicate sortable column
                    verticalAlign: "middle",
                  }}
                  onClick={toggleSort}
                  onMouseOver={e => {
                    e.currentTarget.style.backgroundColor = "#dddddd";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.backgroundColor = "#ebebeb";
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    {flexRender(
                      columns[0].header,
                      table.getHeaderGroups()[0].headers[0].getContext()
                    )}
                  </Box>
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Monday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Tuesday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Wednesday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Thursday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Friday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Saturday")}
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "100px",
                    maxWidth: "100px",
                  }}
                >
                  {translate("Sunday")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredSchedules.map((schedule, index) => {
                const isExpanded = expandedRows.includes(index);

                // Get all unique shifts across all days when expanded
                const uniqueShifts = (() => {
                  if (!isExpanded) {
                    return [];
                  }

                  // Create a map of shifts by ID/name to deduplicate
                  const shiftsMap = new Map();

                  schedule.days.forEach(day => {
                    if (day && day.shifts) {
                      day.shifts.forEach(shift => {
                        // Use name and ordinal as a unique identifier
                        const shiftKey = `${shift.name || ""}__${shift.ordinal}`;
                        if (!shiftsMap.has(shiftKey)) {
                          shiftsMap.set(shiftKey, {
                            ...shift,
                            shiftKey,
                          });
                        }
                      });
                    }
                  });

                  // Convert map to array and sort by ordinal
                  return Array.from(shiftsMap.values()).sort(
                    (a, b) => (a.ordinal || 0) - (b.ordinal || 0)
                  );
                })();

                return (
                  <React.Fragment key={`schedule-${index}`}>
                    {/* Main row with schedule name */}
                    <tr
                      style={{
                        cursor: "pointer",
                        // Apply base background color here for simplicity, cells will override
                        backgroundColor:
                          index % 2 !== 0 ? "#f9f9f9" : undefined,
                      }}
                      onClick={() => toggleRowExpansion(index)}
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      data-testid={`shift-schedule-row-${index}`}
                    >
                      <td
                        style={{
                          padding: "12px",
                          borderBottom: isExpanded
                            ? "1px dashed #ddd"
                            : "1px solid #ddd",
                          verticalAlign: "middle",
                          fontWeight: "bold",
                          backgroundColor: isExpanded
                            ? "rgba(63, 81, 181, 0.15)" // Darker for expanded
                            : hoveredRow === index
                              ? "rgba(63, 81, 181, 0.05)" // Lighter for hover
                              : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              toggleRowExpansion(index);
                            }}
                            size="small"
                            sx={{ mt: -0.5 }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}
                          </IconButton>
                          <ShiftSchedulesHighlightedText
                            text={schedule.configName}
                            highlight={searchTerm}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            width: "fit-content",
                            margin: "0.25rem auto 0",
                            fontSize: "12px",
                          }}
                        >
                          {"("}
                          {schedule.days[0]?.workScheduleConfig?.timeZone}
                          {")"}
                        </Typography>
                      </td>
                      {schedule.days.map((day, dayIndex) => (
                        <td
                          key={dayIndex}
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            borderBottom: isExpanded
                              ? "1px dashed #ddd"
                              : "1px solid #ddd",
                            color: day?.isArchived ? "#999" : "inherit",
                            backgroundColor: isExpanded
                              ? "rgba(63, 81, 181, 0.15)" // Darker for expanded
                              : hoveredRow === index
                                ? "rgba(63, 81, 181, 0.05)" // Lighter for hover
                                : undefined,
                            height: !isExpanded ? "60px" : "auto",
                            verticalAlign: "middle",
                          }}
                          onClick={e => {
                            // Prevent event propagation to avoid double-toggle
                            e.stopPropagation();
                            if (day) {
                              toggleRowExpansion(index);
                            }
                          }}
                          data-expanded={isExpanded ? "true" : "false"}
                        >
                          {day ? (
                            <div>
                              <div>{day.shiftsCount || 0} Shifts</div>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* New expanded sub-rows - one per unique shift */}
                    {isExpanded &&
                      uniqueShifts.map((shift, shiftIndex) => (
                        <tr
                          key={`shift-${index}-${shift.shiftKey}`}
                          style={{
                            backgroundColor: "rgba(63, 81, 181, 0.05)",
                          }}
                        >
                          {/* First column - shift name */}
                          <td
                            style={{
                              padding: "8px 12px 8px 48px", // Indented padding
                              borderBottom:
                                shiftIndex === uniqueShifts.length - 1
                                  ? "1px solid #ddd"
                                  : "1px dashed #ddd",
                              verticalAlign: "middle",
                            }}
                          >
                            <Typography variant="body2">
                              {shift.name || `Shift ${shift.ordinal}`}
                            </Typography>
                          </td>

                          {/* Day columns */}
                          {schedule.days.map((day, dayIndex) => {
                            // Find if this shift exists in this day
                            const dayHasShift =
                              day &&
                              day.shifts &&
                              day.shifts.some(dayShift => {
                                const dayShiftKey = `${dayShift.name || ""}__${dayShift.ordinal}`;
                                return dayShiftKey === shift.shiftKey;
                              });

                            // If found, get the actual shift from this day (might have different times)
                            const shiftInThisDay =
                              dayHasShift &&
                              day.shifts.find(dayShift => {
                                const dayShiftKey = `${dayShift.name || ""}__${dayShift.ordinal}`;
                                return dayShiftKey === shift.shiftKey;
                              });

                            return (
                              <td
                                key={dayIndex}
                                style={{
                                  padding: "8px",
                                  textAlign: "center",
                                  borderBottom:
                                    shiftIndex === uniqueShifts.length - 1
                                      ? "1px solid #ddd"
                                      : "1px dashed #ddd",
                                  verticalAlign: "middle",
                                }}
                              >
                                {dayHasShift && shiftInThisDay ? (
                                  <Box
                                    sx={{
                                      padding: "8px",
                                      border: "1px solid #ddd",
                                      borderRadius: "4px",
                                      backgroundColor: "white",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                      margin: "0 auto",
                                      maxWidth: "120px",
                                      opacity: shiftInThisDay.name
                                        ?.toLowerCase()
                                        .includes("not")
                                        ? 0.6
                                        : 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {shiftInThisDay.startTime || "N/A"} -{" "}
                                      {shiftInThisDay.endTime || "N/A"}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ height: "26px" }}></Box> // Empty box to maintain row height
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
      {process.env.NODE_ENV === "development" && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            * JSON viewer below is only visible in development mode
          </Typography>
          <CustomJsonEditor
            key={`json-editor-work-schedules ${JSON.stringify(workSchedules)}`}
            value={workSchedules}
            height={innerHeight ? innerHeight - 300 : 500}
          />
        </Box>
      )}
    </Paper>
  );
};
